
import { useState } from "react"
import type { ReciboGroup }  from "@/types/analise-previdenciario"
import {
  formatCurrency,
  formatDateTime,
  getNatRubrLabel,
  getNatRubrColor,
  isTotalizadorDeRetorno,
} from "../../../utils/previdenciario-utils"
import { CopyableText } from "./copyable-text"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Receipt,
  Banknote,
  Calculator,
} from "lucide-react"

interface ReciboCardProps {
  group: ReciboGroup
  highlight?: boolean
}

export function ReciboCard({ group, highlight }: ReciboCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  const totalVerbas = group.verbas.reduce(
    (sum, v) => sum + parseFloat(v.value),
    0
  )

  const evento = group.verbas[0]?.evento || "N/A"
  const totalizadorEhRetorno = group.totalizador ? isTotalizadorDeRetorno(group.totalizador) : false

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card
        className={`transition-all ${
          highlight ? "ring-2 ring-chart-1/50 shadow-lg" : "hover:shadow-md"
        }`}
      >
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-xs text-muted-foreground shrink-0">
                      Recibo:
                    </CardTitle>
                    <CopyableText
                      text={group.nrRecibo}
                      className="text-xs"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    ideDmDev:{" "}
                    <span className="font-mono">{group.ideDmDev}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-xs">
                  {evento}
                </Badge>
                {group.totalizador && totalizadorEhRetorno && (
                  <Badge variant="destructive" className="text-xs">
                    Retorno
                  </Badge>
                )}
                {group.totalizador && !totalizadorEhRetorno && (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs">
                    Original
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateTime(group.dhProcessamento)}
                </span>
              </div>
            </div>

            {/* Summary bar */}
            <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                <span>{group.rubricas.length} rubricas</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Receipt className="h-3.5 w-3.5" />
                <span>{group.verbas.length} verbas</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Banknote className="h-3.5 w-3.5" />
                <span>{group.pagamentos.length} pagamentos</span>
              </div>
              {totalVerbas > 0 && (
                <div className="flex items-center gap-1.5 ml-auto font-semibold text-foreground">
                  <Calculator className="h-3.5 w-3.5" />
                  <span>Total verbas: {formatCurrency(totalVerbas)}</span>
                </div>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* Rubricas */}
            {group.rubricas.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  Rubricas
                </h4>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Cod</TableHead>
                        <TableHead className="text-xs">Descricao</TableHead>
                        <TableHead className="text-xs">Natureza</TableHead>
                        <TableHead className="text-xs">Tab</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.rubricas.map((r, i) => (
                        <TableRow key={`${r.codRubr}-${i}`}>
                          <TableCell className="font-mono text-xs">
                            {r.codRubr}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.dscrRubrica}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={`text-[10px] ${getNatRubrColor(
                                r.natRubr
                              )}`}
                            >
                              {getNatRubrLabel(r.natRubr)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {r.ideTabRubr}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Verbas */}
            {group.verbas.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Receipt className="h-3.5 w-3.5" />
                  Verbas
                </h4>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Evento</TableHead>
                        <TableHead className="text-xs">Cod. rubrica</TableHead>
                        <TableHead className="text-xs">ideDmDev</TableHead>
                        <TableHead className="text-xs text-right">
                          Valor
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.verbas.map((v, i) => (
                        <TableRow key={`${v.nrRecibo_verba}-${i}`}>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px]">
                              {v.evento}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {v.codRubr || "-"}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {v.ideDmDev}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-xs">
                            {formatCurrency(v.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/30 font-semibold">
                        <TableCell colSpan={3} className="text-xs">
                          Total
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {formatCurrency(totalVerbas)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Pagamentos */}
            {group.pagamentos.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Banknote className="h-3.5 w-3.5" />
                  Pagamentos vinculados
                </h4>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs min-w-[260px]">
                          Nr Recibo Pgto
                        </TableHead>
                        <TableHead className="text-xs">CPF</TableHead>
                        <TableHead className="text-xs text-right">
                          Valor Liquido
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.pagamentos.map((p, i) => (
                        <TableRow key={`${p.nrRecibo}-${i}`}>
                          <TableCell>
                            <CopyableText
                              text={p.nrRecibo}
                              className="text-[11px]"
                            />
                          </TableCell>
                          <TableCell>
                            <CopyableText
                              text={p.cpfBeneficiario}
                              className="text-[11px]"
                            />
                          </TableCell>
                          <TableCell className="text-right font-semibold text-xs">
                            {formatCurrency(p.vrLiq)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Totalizador inline */}
            {group.totalizador && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Calculator className="h-3.5 w-3.5" />
                  Totalizador
                </h4>
                <div className="mb-2">
                  <span className="text-[10px] text-muted-foreground uppercase mr-2">
                    CPF Trabalhador:
                  </span>
                  <CopyableText
                    text={group.totalizador.cpfTrabalhador}
                    className="text-xs"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Valor
                    </p>
                    <p className="text-sm font-semibold">
                      {formatCurrency(group.totalizador.valor)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Valor 13o
                    </p>
                    <p className="text-sm font-semibold">
                      {formatCurrency(group.totalizador.valor_13)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      CP Segurado
                    </p>
                    <p className="text-sm font-semibold">
                      {formatCurrency(group.totalizador.vrcpseg)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Desc Segurado
                    </p>
                    <p className="text-sm font-semibold">
                      {formatCurrency(group.totalizador.vrdescseg)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
